<template>
  <div class="rs-page">
    <v-form ref="form" lazy-validation>
      <!-- Toolbar -->
      <div class="rs-toolbar">
        <div class="rs-toolbar__inner">
          <v-select
            v-model="form.partner_id"
            :items="partnerItems"
            item-text="display_name"
            item-value="id"
            label="Partner"
            outlined
            dense
            hide-details
            :loading="loading"
            :disabled="loading || saving"
            class="rs-toolbar__partner"
            @change="onPartnerChange"
          />

          <v-select
            v-model="form.program_batch_id"
            :items="batchItems"
            item-text="label"
            item-value="id"
            label="Batch"
            outlined
            dense
            hide-details
            :loading="loading"
            :disabled="loading || saving || !form.partner_id"
            class="rs-toolbar__batch"
            @change="onBatchChange"
          />

          <v-select
            v-model="categoryFilter"
            :items="categoryItems"
            item-text="text"
            item-value="value"
            label="Category"
            outlined
            dense
            hide-details
            :disabled="loading || saving || !form.partner_id"
            class="rs-toolbar__category"
          />

          <v-chip v-if="type" x-small outlined class="rs-chip">
            <v-icon x-small left>mdi-tag-outline</v-icon>
            {{ type }}
          </v-chip>

          <!--
          <v-text-field
            v-if="form.partner_id && !loading && groupedSplits.length"
            v-model="search"
            placeholder="Filter programmes"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            class="rs-toolbar__search"
          />-->

          <v-spacer />

          <template v-if="form.partner_id && !loading">
            <div
              v-if="hasExistingSplits && groupedSplits.length"
              class="rs-progress"
            >
              <div class="rs-progress__head">
                <span class="rs-progress__count">
                  {{ completedCount }}/{{ visibleGroups.length }}
                </span>
                <span class="rs-progress__label">programmes configured</span>
              </div>
              <v-progress-linear
                :value="progressPercent"
                :color="progressPercent === 100 ? 'success' : 'primary'"
                background-color="#eceff1"
                height="4"
                rounded
                class="rs-progress__bar"
              />
            </div>

            <v-btn
              v-if="hasExistingSplits && groupedSplits.length"
              small
              text
              class="ml-2"
              :disabled="saving"
              @click="toggleAll"
            >
              <v-icon small left>
                {{
                  allExpanded
                    ? "mdi-unfold-less-horizontal"
                    : "mdi-unfold-more-horizontal"
                }}
              </v-icon>
              {{ allExpanded ? "Collapse all" : "Expand all" }}
            </v-btn>

            <v-btn
              v-if="!hasExistingSplits"
              small
              depressed
              color="primary"
              class="ml-2"
              :disabled="!partner_programs.length || !feeTypes.length"
              @click="initializeSplits"
            >
              <v-icon small left>mdi-plus</v-icon>
              Create Splits
            </v-btn>

            <v-btn
              v-if="hasExistingSplits && currentSplits.length > 0"
              small
              outlined
              color="primary"
              class="ml-2"
              @click="showSummary"
            >
              <v-icon small left>mdi-chart-box-outline</v-icon>
              Summary
            </v-btn>
          </template>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="rs-state">
        <v-progress-circular
          indeterminate
          color="primary"
          size="40"
          width="3"
        />
        <p class="rs-state__text">Loading revenue splits…</p>
      </div>

      <template v-else>
        <!-- Empty: nothing created yet -->
        <v-card
          v-if="form.partner_id && !hasExistingSplits"
          outlined
          class="rs-state rs-state--card"
        >
          <v-icon size="44" color="grey lighten-1"
            >mdi-file-percent-outline</v-icon
          >
          <h3 class="rs-state__title">No revenue splits yet</h3>
          <p class="rs-state__text">
            No revenue splits have been created for this partner yet.
            <template v-if="!partner_programs.length || !feeTypes.length">
              <br />Programmes or fee types are unavailable for this partner.
            </template>
          </p>
          <v-btn
            v-if="partner_programs.length && feeTypes.length"
            small
            depressed
            color="primary"
            class="mt-3"
            @click="initializeSplits"
          >
            <v-icon small left>mdi-plus</v-icon>
            Create Splits
          </v-btn>
        </v-card>

        <!-- Empty: created but no programmes/fee types -->
        <v-card
          v-else-if="
            form.partner_id && hasExistingSplits && !groupedSplits.length
          "
          outlined
          class="rs-state rs-state--card"
        >
          <v-icon size="44" color="grey lighten-1"
            >mdi-alert-circle-outline</v-icon
          >
          <h3 class="rs-state__title">Nothing to configure</h3>
          <p class="rs-state__text">
            No programmes found for this partner, or the fee type configuration
            is missing.
          </p>
        </v-card>

        <!-- Empty: filter matched nothing -->
        <v-card
          v-else-if="groupedSplits.length && !visibleGroups.length"
          outlined
          class="rs-state rs-state--card"
        >
          <v-icon size="36" color="grey lighten-1">mdi-magnify-close</v-icon>
          <p class="rs-state__text mt-2">{{ noMatchMessage }}</p>
        </v-card>

        <!-- Programme panels -->
        <v-expansion-panels
          v-else-if="visibleGroups.length"
          v-model="panel"
          multiple
          accordion
          flat
          class="rs-panels"
        >
          <v-expansion-panel
            v-for="group in visibleGroups"
            :key="group.program_id"
            class="rs-panel"
            :class="isPending(group) ? 'rs-panel--pending' : 'rs-panel--done'"
          >
            <v-expansion-panel-header class="rs-panel__header">
              <div class="rs-panel__title">
                <span class="rs-panel__name">{{
                  getProgramLabel(group.program_id)
                }}</span>
                <span class="rs-panel__meta">
                  {{ configuredCount(group) }}/{{ groupRows(group).length }}
                </span>
              </div>
              <template #actions>
                <div class="rs-panel__status">
                  <v-chip
                    x-small
                    label
                    :color="isPending(group) ? 'warning' : 'success'"
                    text-color="white"
                    class="rs-status-chip"
                  >
                    {{ isPending(group) ? "Pending" : "Configured" }}
                  </v-chip>
                  <v-icon small class="ml-1">mdi-chevron-down</v-icon>
                </div>
              </template>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="rs-panel__content">
              <!-- Copy from another programme -->
              <div v-if="copyableFrom(group.program_id).length" class="rs-copy">
                <span class="rs-copy__label">Apply same split as</span>
                <v-select
                  v-model="copyProgram[group.program_id]"
                  :items="copyableFrom(group.program_id)"
                  item-text="name"
                  item-value="id"
                  label="Programme"
                  outlined
                  dense
                  hide-details
                  class="rs-copy__select"
                />
                <v-btn
                  small
                  depressed
                  color="primary"
                  :disabled="!copyProgram[group.program_id]"
                  @click="
                    applySameSplit(
                      group.program_id,
                      copyProgram[group.program_id],
                    )
                  "
                >
                  Apply
                </v-btn>
              </div>

              <div class="rs-table-wrap">
                <table class="rs-table">
                  <thead>
                    <tr>
                      <th class="rs-th">Fee Type</th>
                      <th class="rs-th rs-th--num">Share %</th>
                      <th class="rs-th rs-th--num">GST %</th>
                      <th class="rs-th">GST Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <div v-for="section in categorySections(group)">
                    <tr
                      :key="`cat-${group.program_id}-${section.category}`"
                      class="rs-cat-row"
                    >
                      <td class="rs-cat" colspan="4">
                        <span class="rs-cat__name">{{ section.category }}</span>
                        <span class="rs-cat__meta">
                          {{ sectionConfiguredCount(section) }}/{{
                            section.items.length
                          }}
                        </span>
                      </td>
                    </tr>
                    <tr
                      v-for="{ row, index } in section.items"
                      :key="`${group.program_id}-${row.fee_code}`"
                    >
                      <td class="rs-td">
                        <span class="rs-fee">
                          <span class="rs-fee__code">{{ row.fee_code }}</span>
                          <span class="rs-fee__name">{{
                            getFeeLabel(row.fee_code)
                          }}</span>
                        </span>
                      </td>
                      <td class="rs-td rs-td--num">
                        <v-text-field
                          v-model.number="row.fee_percentage"
                          :ref="`fee-${group.program_id}-${index}`"
                          suffix="%"
                          min="0"
                          max="100"
                          dense
                          outlined
                          hide-details
                          :disabled="saving"
                          :rules="[rules.percentage]"
                          class="rs-input"
                          @input="onFeeInput(row, group.program_id, index)"
                        />
                      </td>
                      <td class="rs-td rs-td--num">
                        <v-text-field
                          v-model.number="row.gst_percentage"
                          :ref="`gst-${group.program_id}-${index}`"
                          suffix="%"
                          min="0"
                          max="100"
                          dense
                          outlined
                          hide-details
                          :disabled="saving || !Number(row.fee_percentage)"
                          :rules="[
                            rules.percentage,
                            rules.gstLessThanFee(row.fee_percentage),
                          ]"
                          class="rs-input"
                          @input="
                            triggerValidation('fee', group.program_id, index)
                          "
                        />
                      </td>
                      <td class="rs-td rs-td--gst">
                        <v-radio-group
                          v-if="Number(row.gst_percentage) > 0"
                          v-model="row.gst_type"
                          row
                          dense
                          hide-details
                          :disabled="saving"
                          :rules="[rules.required]"
                          class="rs-radio"
                        >
                          <v-radio label="Incl." value="Inclusive" />
                          <v-radio label="Excl." value="Exclusive" />
                        </v-radio-group>
                        <span v-else class="rs-muted">—</span>
                      </td>
                    </tr>
                  </div>
                  </tbody>
                </table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel> 
        </v-expansion-panels>

        <!-- Sticky actions -->
        <div
          v-if="hasExistingSplits && groupedSplits.length"
          class="rs-actions"
        >
          <span v-if="isDirty" class="rs-actions__hint">
            <v-icon x-small color="warning" class="mr-1"
              >mdi-circle-medium</v-icon
            >
            Unsaved changes
          </span>
          <v-btn small text :disabled="!isDirty || saving" @click="resetChanges"
            >Reset</v-btn
          >
          <v-btn
            small
            depressed
            color="primary"
            class="ml-2"
            :disabled="!isDirty"
            :loading="saving"
            @click="submitForm"
          >
            Save
          </v-btn>
        </div>
      </template>
    </v-form>
  </div>
</template>

<script>
import { rules } from "@/helpers/rules";

// Service categories the settlement is reported under. Fee types are grouped
// under these, so every fee code must resolve to exactly one of them.
const FEE_CATEGORIES = [
  "Admission Service",
  "Academic Support Service",
  "Online Education Support Service",
  "Examination Support Service",
];

// Fee types that have not been tagged yet still have to be configurable, so
// they fall into this bucket instead of disappearing from the table.
const UNCATEGORISED = "Uncategorised";

// Fee types carry their own `category` once tagged in Fee Types master. Until
// then the category is derived from the fee name/code, keyed by the fee code
// first and falling back to name keywords.
const CATEGORY_BY_FEE_CODE = {};

const CATEGORY_KEYWORDS = [
  ["Admission Service", ["admission", "registration", "application", "enrol"]],
  ["Academic Support Service", ["tuition", "term", "annual", "academic"]],
  [
    "Online Education Support Service",
    ["online", "lms", "technology", "platform", "digital"],
  ],
  [
    "Examination Support Service",
    ["exam", "convocation", "revaluation", "re-valuation", "marksheet"],
  ],
];

export default {
  data() {
    return {
      form: {
        partner_id: null,
        program_batch_id: null,
        program_splits: [],
      },
      partner_programs: [],
      programBatches: [],
      type: "",
      partners: [],
      allFeeTypes: [],
      hasExistingSplits: false,
      loading: false,
      saving: false,
      rules,
      currentSplits: [],
      panel: [],
      copyProgram: {},
      snapshot: "[]",
      search: "",
      categoryFilter: null,
      currentRequest: 0,
    };
  },

  watch: {
    // Panel indexes are positions in visibleGroups, so they stop meaning
    // anything once a filter changes the list.
    search() {
      this.panel = [];
    },

    categoryFilter() {
      this.panel = [];
    },
  },

  computed: {
    partnerItems() {
      return this.partners.map((partner) => ({
        ...partner,
        display_name: partner.name,
      }));
    },

    // Fee types depend on the selected partner's type, so derive them instead
    // of filtering once at load time. Self partners settle on every fee type,
    // so no filter applies to them.
    feeTypes() {
      if (this.type === "Self") return this.allFeeTypes;
      if (this.type === "Business") {
        return this.allFeeTypes.filter((fee) => fee.is_business === true);
      }
      return [];
    },

    // A batch belongs to one programme, so only the selected partner's
    // programmes can be configured batch-wise.
    batchItems() {
      const programIds = this.partner_programs.map((program) => program.id);
      const batches = this.programBatches
        .filter((batch) => programIds.includes(batch.program_id))
        .map((batch) => ({
          id: batch.id,
          program_id: batch.program_id,
          label: batch.name || `${batch.year}-${batch.batch}`,
        }));

      return [{ id: null, program_id: null, label: "All Batches" }, ...batches];
    },

    categoryItems() {
      return [
        { text: "All Categories", value: null },
        ...this.availableCategories.map((category) => ({
          text: category,
          value: category,
        })),
      ];
    },

    // Only offer categories that the current partner's fee types actually map
    // to, so the filter never selects an empty result.
    availableCategories() {
      const present = new Set(
        this.feeTypes.map((fee) => this.getFeeCategory(fee)),
      );
      return [...FEE_CATEGORIES, UNCATEGORISED].filter((category) =>
        present.has(category),
      );
    },

    groupedSplits() {
      const grouped = {};
      this.form.program_splits.forEach((row) => {
        if (!grouped[row.program_id]) {
          grouped[row.program_id] = { program_id: row.program_id, rows: [] };
        }
        grouped[row.program_id].rows.push(row);
      });
      return Object.values(grouped);
    },

    visibleGroups() {
      const term = (this.search || "").trim().toLowerCase();
      return this.groupedSplits.filter((group) => {
        if (!this.groupRows(group).length) return false;
        if (!term) return true;
        return this.getProgramLabel(group.program_id)
          .toLowerCase()
          .includes(term);
      });
    },

    noMatchMessage() {
      const term = (this.search || "").trim();
      if (term) return `No programme matches “${term}”.`;
      return `No fee type falls under “${this.categoryFilter}” for this partner.`;
    },

    completedCount() {
      return this.visibleGroups.filter((group) => !this.isPending(group))
        .length;
    },

    progressPercent() {
      if (!this.visibleGroups.length) return 0;
      return Math.round((this.completedCount / this.visibleGroups.length) * 100);
    },

    allExpanded() {
      return (
        this.visibleGroups.length > 0 &&
        this.panel.length === this.visibleGroups.length
      );
    },

    isDirty() {
      return JSON.stringify(this.form.program_splits) !== this.snapshot;
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Partner Revenue Split" }]);
    await Promise.all([this.getFeeTypes(), this.getPartners()]);
  },

  beforeDestroy() {
    this.currentRequest = -1;
  },

  methods: {
    async getPartners() {
      this.loading = true;
      try {
        const res = await this.$axios.get("partners");
        this.partners = res.data.data.filter((p) => p.type !== "Sponsor");
        this.loading = false;
        // if (this.partners.length) {
        // this.form.partner_id = this.partners[0].id;
        // await this.onPartnerChange(this.form.partner_id);
        // }
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async getFeeTypes() {
      try {
        const res = await this.$axios.get("fee_types");
        this.allFeeTypes = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load fee types");
      }
    },

    async onPartnerChange(id) {
      this.loading = true;
      this.currentRequest++;
      const requestId = this.currentRequest;

      this.form.partner_id = id;
      this.form.program_batch_id = null;
      this.form.program_splits = [];
      this.partner_programs = [];
      this.currentSplits = [];
      this.hasExistingSplits = false;
      this.copyProgram = {};
      this.panel = [];
      this.search = "";
      this.categoryFilter = null;

      const selected = this.partners.find((p) => p.id === id);
      this.type = selected?.type || "";

      try {
        await Promise.all([this.getPartnerPrograms(), this.getProgramBatches()]);

        if (this.currentRequest !== requestId) return;

        await this.loadSplits(requestId);
      } catch (err) {
        this.notifyError(err, "Unable to load revenue splits");
      } finally {
        if (this.currentRequest === requestId) this.loading = false;
      }
    },

    // Splits are configured per batch. "All Batches" (null) holds the partner's
    // default, and a batch selection holds the override for that batch only.
    async onBatchChange(batchId) {
      this.loading = true;
      this.currentRequest++;
      const requestId = this.currentRequest;

      this.form.program_batch_id = batchId ?? null;
      this.form.program_splits = [];
      this.currentSplits = [];
      this.hasExistingSplits = false;
      this.copyProgram = {};
      this.panel = [];

      try {
        await this.loadSplits(requestId);
      } catch (err) {
        this.notifyError(err, "Unable to load revenue splits");
      } finally {
        if (this.currentRequest === requestId) this.loading = false;
      }
    },

    async loadSplits(requestId) {
      const batchId = this.form.program_batch_id ?? null;
      const params = { partner_id: this.form.partner_id };
      if (batchId) params.program_batch_id = batchId;

      const res = await this.$axios.get("/revenue_split_config", { params });

      if (this.currentRequest !== requestId) return;

      // The endpoint scopes by batch already; scoping again here keeps a batch
      // override from ever being merged into the partner default.
      const savedSplits = (res.data.data || []).filter(
        (split) => (split.program_batch_id ?? null) === batchId,
      );

      this.currentSplits = savedSplits;
      this.hasExistingSplits = savedSplits.length > 0;

      const mergedSplits = [];
      this.scopedPrograms().forEach((program) => {
        this.feeTypes.forEach((fee) => {
          const existing = savedSplits.find(
            (s) => s.program_id === program.id && s.fee_code === fee.code,
          );

          const gstType = existing?.gst_type;
          const validGstType =
            gstType && ["Inclusive", "Exclusive"].includes(gstType)
              ? gstType
              : "Exclusive";

          mergedSplits.push({
            id: existing?.id,
            program_id: program.id,
            program_batch_id: batchId,
            fee_code: fee.code,
            category: this.getFeeCategory(fee),
            fee_percentage: existing ? Number(existing.fee_percentage) : 0,
            gst_percentage: existing ? Number(existing.gst_percentage) : 0,
            is_gst: existing ? Number(existing.gst_percentage) > 0 : false,
            gst_type:
              existing && Number(existing.gst_percentage) > 0
                ? validGstType
                : "",
          });
        });
      });

      this.form.program_splits = mergedSplits;
      this.takeSnapshot();
      const pendingIndex = this.visibleGroups.findIndex((g) =>
        this.isPending(g),
      );
      this.panel = this.visibleGroups.length
        ? [pendingIndex > -1 ? pendingIndex : 0]
        : [];
    },

    // A batch belongs to a single programme, so selecting one narrows the
    // configuration down to that programme.
    scopedPrograms() {
      const batchId = this.form.program_batch_id ?? null;
      if (!batchId) return this.partner_programs;

      const batch = this.programBatches.find((item) => item.id === batchId);
      if (!batch) return this.partner_programs;

      return this.partner_programs.filter(
        (program) => program.id === batch.program_id,
      );
    },

    async getProgramBatches() {
      try {
        const res = await this.$axios.get("/program_batches?active=true");
        this.programBatches = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load batches");
        this.programBatches = [];
      }
    },

    async getPartnerPrograms() {
      if (!this.form.partner_id) return;
      try {
        const url =
          this.type === "Self"
            ? "/programs?active=true"
            : `/get_partner_programs/${this.form.partner_id}`;
        const res = await this.$axios.get(url);
        this.partner_programs = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load partner programmes");
        this.partner_programs = [];
      }
    },

    initializeSplits() {
      const splits = [];
      this.scopedPrograms().forEach((program) => {
        this.feeTypes.forEach((fee) => {
          splits.push({
            program_id: program.id,
            program_batch_id: this.form.program_batch_id ?? null,
            fee_code: fee.code,
            category: this.getFeeCategory(fee),
            fee_percentage: 0,
            gst_percentage: 0,
            is_gst: false,
            gst_type: "",
          });
        });
      });
      this.form.program_splits = splits;
      this.hasExistingSplits = true;
      this.snapshot = "[]";
      this.panel = splits.length ? [0] : [];
    },

    takeSnapshot() {
      this.snapshot = JSON.stringify(this.form.program_splits);
    },

    resetChanges() {
      this.onBatchChange(this.form.program_batch_id);
    },

    toggleAll() {
      this.panel = this.allExpanded
        ? []
        : this.visibleGroups.map((_, index) => index);
    },

    getProgramLabel(program_id) {
      const prog = this.partner_programs.find((p) => p.id === program_id);
      if (!prog) return `Programme #${program_id}`;
      const name = prog.degree?.short_name || prog.name || "";
      return `${name}${prog.specialization ? " - " + prog.specialization : ""}`;
    },

    getFeeLabel(code) {
      const fee = this.feeTypes.find((f) => f.code === code);
      return fee?.name || code;
    },

    // Category comes from the fee type master once tagged there; otherwise it
    // is derived from the code map and then from name keywords.
    getFeeCategory(fee) {
      if (!fee) return UNCATEGORISED;
      if (FEE_CATEGORIES.includes(fee.category)) return fee.category;
      if (CATEGORY_BY_FEE_CODE[fee.code]) return CATEGORY_BY_FEE_CODE[fee.code];

      const haystack = `${fee.name || ""} ${fee.code || ""}`.toLowerCase();
      const match = CATEGORY_KEYWORDS.find(([, keywords]) =>
        keywords.some((keyword) => haystack.includes(keyword)),
      );
      return match ? match[0] : UNCATEGORISED;
    },

    getRowCategory(row) {
      if (row.category) return row.category;
      return this.getFeeCategory(
        this.feeTypes.find((fee) => fee.code === row.fee_code),
      );
    },

    // Rows of a programme narrowed down to the selected category.
    groupRows(group) {
      const rows = group.rows || [];
      if (!this.categoryFilter) return rows;
      return rows.filter(
        (row) => this.getRowCategory(row) === this.categoryFilter,
      );
    },

    // Fee rows of a programme grouped under their service category, keeping
    // each row's index in group.rows so the validation refs stay stable.
    categorySections(group) {
      const order = [...FEE_CATEGORIES, UNCATEGORISED];
      const sections = new Map();

      (group.rows || []).forEach((row, index) => {
        const category = this.getRowCategory(row);
        if (this.categoryFilter && category !== this.categoryFilter) return;
        if (!sections.has(category)) sections.set(category, []);
        sections.get(category).push({ row, index });
      });

      return order
        .filter((category) => sections.has(category))
        .map((category) => ({ category, items: sections.get(category) }));
    },

    sectionConfiguredCount(section) {
      return section.items.filter(
        ({ row }) => Number(row.fee_percentage) > 0,
      ).length;
    },

    isPending(group) {
      const rows = this.groupRows(group);
      if (!rows.length) return true;
      return rows.every((row) => !Number(row.fee_percentage));
    },

    configuredCount(group) {
      return this.groupRows(group).filter(
        (row) => Number(row.fee_percentage) > 0,
      ).length;
    },

    onFeeInput(row, programId, index) {
      // Share of 0 means the fee is not settled to this partner, so GST cannot apply.
      if (!Number(row.fee_percentage)) {
        row.gst_percentage = 0;
        row.gst_type = "";
      }
      this.triggerValidation("gst", programId, index);
    },

    triggerValidation(target, programId, index) {
      this.$nextTick(() => {
        let ref = this.$refs[`${target}-${programId}-${index}`];
        if (Array.isArray(ref)) ref = ref[0];
        if (ref && typeof ref.validate === "function") ref.validate();
      });
    },

    copyableFrom(program_id) {
      return this.groupedSplits
        .filter(
          (group) =>
            group.program_id !== program_id &&
            this.groupRows(group).some((row) => Number(row.fee_percentage) > 0),
        )
        .map((group) => ({
          id: group.program_id,
          name: this.getProgramLabel(group.program_id),
        }));
    },

    applySameSplit(program_id, copyProgram) {
      if (!copyProgram) return;

      const groupToCopy = this.groupedSplits.find(
        (g) => g.program_id === copyProgram,
      );
      const groupToUpdate = this.groupedSplits.find(
        (g) => g.program_id === program_id,
      );
      if (!groupToCopy || !groupToUpdate) return;

      // With a category filter on, only the rows on screen are overwritten.
      this.groupRows(groupToUpdate).forEach((row) => {
        const source = groupToCopy.rows.find(
          (r) => r.fee_code === row.fee_code,
        );
        if (!source) return;
        row.fee_percentage = source.fee_percentage;
        row.gst_percentage = source.gst_percentage;
        row.gst_type = source.gst_type;
        row.is_gst = Number(source.gst_percentage) > 0;
      });

      this.copyProgram = { ...this.copyProgram, [program_id]: null };
      this.$store.commit("showSnackbar", {
        message: `Split configuration copied successfully`,
        color: "success",
      });
    },

    showSummary() {
      if (!this.currentSplits.length) {
        this.$store.commit("showSnackbar", {
          message: "No splits configured yet",
          color: "error",
        });
        return;
      }
      this.$router.push("/revenue-split-summary/" + this.form.partner_id);
    },

    submitForm() {
      if (!this.$refs.form.validate()) {
        this.$store.commit("showSnackbar", {
          message: "Please correct the details and try again",
          color: "error",
        });
        return;
      }

      this.saving = true;

      const payload = {
        partner_id: this.form.partner_id,
        program_batch_id: this.form.program_batch_id ?? null,
        program_splits: this.form.program_splits.map((split) => {
          const fee_percentage = Number(split.fee_percentage) || 0;
          const gst_percentage = fee_percentage
            ? Number(split.gst_percentage) || 0
            : 0;

          const item = {
            program_id: split.program_id,
            program_batch_id: split.program_batch_id ?? null,
            partner_id: this.form.partner_id,
            fee_code: split.fee_code,
            category: this.getRowCategory(split),
            fee_percentage,
            gst_percentage,
            is_gst: gst_percentage > 0,
            gst_type: gst_percentage > 0 ? split.gst_type : null,
          };

          // Keep the id so existing rows are updated instead of duplicated.
          if (split.id) item.id = split.id;

          return item;
        }),
      };

      this.$axios
        .put("/revenue_split_config", payload)
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Saved successfully",
            color: "success",
          });
          // Reload the same batch scope so newly inserted rows come back with ids.
          return this.onBatchChange(this.form.program_batch_id);
        })
        .catch((err) => this.notifyError(err, "Error saving revenue splits"))
        .finally(() => {
          this.saving = false;
        });
    },

    notifyError(err, fallback) {
      const data = err?.response?.data;
      // Changeset failures come back as { errors: { field: [msg] } }.
      const changesetError = data?.errors
        ? Object.entries(data.errors)
            .map(([field, msgs]) => `${field} ${[].concat(msgs).join(", ")}`)
            .join("; ")
        : null;

      this.$store.commit("showSnackbar", {
        message: data?.message || changesetError || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.rs-page {
  --rs-primary: var(--v-primary-base, #00aff1);
  --rs-line: #e4e7ec;
  --rs-ink: #101828;
  --rs-ink-2: #475467;
  --rs-ink-3: #98a2b3;
  padding: 10px 14px 72px;
  margin: 0px 15px;
  min-height: 100%;
}

/* Toolbar */
.rs-toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #fff;
  border-bottom: 1px solid var(--rs-line);
  margin: -10px -14px 0;
}

.rs-toolbar__inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 14px;
}

.rs-toolbar__partner {
  max-width: 230px;
  flex: 0 1 230px;
}

.rs-toolbar__batch {
  max-width: 200px;
  flex: 0 1 200px;
}

.rs-toolbar__category {
  max-width: 230px;
  flex: 0 1 230px;
}

.rs-toolbar__search {
  max-width: 220px;
  flex: 0 1 220px;
}

.rs-chip {
  border-color: var(--rs-line);
  color: var(--rs-ink-2);
  font-weight: 600;
}

.rs-progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 150px;
}

.rs-progress__head {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.rs-progress__count {
  font-size: 15px;
  font-weight: 700;
  color: var(--rs-ink);
  font-variant-numeric: tabular-nums;
}

.rs-progress__label {
  font-size: 12px;
  color: var(--rs-ink-2);
}

.rs-progress__bar {
  border-radius: 99px;
}

/* States */
.rs-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
}

.rs-state--card {
  border-radius: 8px;
  background: #fff;
  margin-top: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rs-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--rs-ink);
}

.rs-state__text {
  margin: 0;
  font-size: 13px;
  color: var(--rs-ink-2);
  line-height: 1.55;
}

/* Panels */
.rs-panels {
  margin-top: 12px;
  border: 1px solid var(--rs-line);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rs-panel {
  background: #fff;
  position: relative;
}

.rs-panel::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  z-index: 1;
  transition: background 0.2s;
}

.rs-panel--pending::before {
  background: #f79009;
}

.rs-panel--done::before {
  background: #12b76a;
}

.rs-panel:not(:last-child) {
  border-bottom: 1px solid #f0f1f3;
}

.rs-panel__header {
  min-height: 38px !important;
  padding: 4px 12px 4px 14px;
}

.rs-panel__header:hover {
  background: #fafbfc;
}

.rs-panel__title {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
}

.rs-panel__name {
  font-size: 13px;
  font-weight: 600;
  color: var(--rs-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rs-panel__meta {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: var(--rs-ink-3);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.rs-panel__status {
  display: flex;
  align-items: center;
}

.rs-status-chip {
  font-size: 10px !important;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.rs-panel__content {
  background: #fafbfc;
  border-top: 1px solid #f0f1f3;
}

.rs-panel__content ::v-deep .v-expansion-panel-content__wrap {
  padding: 8px 12px 12px;
}

/* Copy row */
.rs-copy {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.rs-copy__label {
  font-size: 12px;
  color: #616161;
}

.rs-copy__select {
  max-width: 210px;
  background: #fff;
}

/* Table */
.rs-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--rs-line);
  border-radius: 8px;
  background: #fff;
}

.rs-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
}

.rs-th {
  padding: 6px 12px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--rs-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--rs-line);
  white-space: nowrap;
}

.rs-th--num {
  text-align: right;
  width: 120px;
}

.rs-td {
  padding: 4px 12px;
  font-size: 12.5px;
  color: var(--rs-ink-2);
  border-bottom: 1px solid #f4f5f7;
  vertical-align: middle;
}

.rs-table tbody tr {
  transition: background 0.15s;
}

.rs-table tbody tr:hover {
  background: #fcfcfd;
}

.rs-table tbody tr:last-child .rs-td {
  border-bottom: none;
}

.rs-td--num > * {
  margin-left: auto;
}

.rs-td--gst {
  width: 175px;
}

/* Category band */
.rs-cat-row:hover {
  background: transparent !important;
}

.rs-cat {
  padding: 5px 12px;
  background: #f4f6f8;
  border-bottom: 1px solid var(--rs-line);
  border-top: 1px solid var(--rs-line);
}

.rs-cat__name {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--rs-ink);
}

.rs-cat__meta {
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  color: var(--rs-ink-3);
  font-variant-numeric: tabular-nums;
}

.rs-fee {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rs-fee__code {
  display: inline-block;
  min-width: 42px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #eef2f6;
  color: #344054;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.rs-fee__name {
  color: var(--rs-ink);
  font-weight: 500;
}

.rs-muted {
  color: var(--rs-ink-3);
}

.rs-input {
  width: 96px;
}

.rs-input ::v-deep input {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

/* Shrink Vuetify's dense field to table-row height. */
.rs-input ::v-deep .v-input__slot {
  min-height: 30px !important;
  padding: 0 8px !important;
}

.rs-input ::v-deep .v-text-field__slot,
.rs-input ::v-deep .v-text-field__suffix {
  font-size: 12.5px;
}

.rs-input ::v-deep .v-input__append-inner {
  margin-top: 4px !important;
}

/* Zero share reads as "not settled" — de-emphasise it. */
.rs-input ::v-deep .v-input--is-disabled input {
  color: var(--rs-ink-3);
}

.rs-radio {
  margin-top: 0;
  padding-top: 0;
}

.rs-radio ::v-deep .v-radio {
  margin-right: 10px !important;
}

.rs-radio ::v-deep .v-label {
  font-size: 12.5px;
}

.rs-radio ::v-deep .v-input--selection-controls__input {
  margin-right: 2px;
  height: 22px;
  width: 22px;
}

.rs-radio ::v-deep .v-icon {
  font-size: 17px;
}

/* Actions — always fixed to the bottom of the viewport, clear of the nav drawer. */
.rs-actions {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin: 0;
  padding: 10px 24px;
  background: #fff;
  border-top: 1px solid var(--rs-line);
  box-shadow: 0 -4px 16px -8px rgba(16, 24, 40, 0.22);
} 


/* Permanent navigation drawer starts at the md breakpoint. */
@media (min-width: 960px) {
  .rs-actions {
    left: 268px;
  }
}

.rs-actions__hint {
  display: flex;
  align-items: center;
  margin-right: auto;
  font-size: 12px;
  font-weight: 600;
  color: #b54708;
}

@media (max-width: 600px) {
  .rs-page {
    padding: 10px 10px 72px;
  }

  .rs-toolbar {
    position: static;
    margin: -10px -10px 0;
  }

  .rs-toolbar__inner {
    padding: 8px 10px;
  }

  .rs-actions {
    padding: 10px 14px;
  }

  .rs-toolbar__partner,
  .rs-toolbar__batch,
  .rs-toolbar__category,
  .rs-toolbar__search {
    max-width: none;
    flex: 1 1 100%;
  }

  .rs-progress {
    min-width: 100%;
  }

  .rs-panel__meta {
    display: none;
  }
}
</style>
